import React, { useState } from "react";
import style from "./index.module.scss";
import {
    BaseQuestionType,
    BaseQuestionTypeMap,
    checkQuestion,
    OptionsType,
    QuestionType
} from "service/exercise";
import { Button, Modal } from "antd";
import classNames from "classnames";
import { htmlToPlain, indexToChar, replaceImgTag, strCut } from "utils/common";
import AddTopicModal from "../../../../index";
import useModalHandler from "utils/useModalHandler";
import { SortableHandle } from "react-sortable-hoc";
import SubModalTitle from "../SubModalTitle";

interface SubTopicItemProps {
    index: number;
    value?: QuestionType;
    subTypes: BaseQuestionType[];
    onChange?: (value: QuestionType) => void;
    onDelete: () => void;
    onUpdate: () => void;
}

export default function SubTopicItem({
    index,
    value,
    subTypes,
    onChange,
    onDelete,
    onUpdate
}: SubTopicItemProps) {
    const [expand, setExpand] = useState(false);
    const modal = useModalHandler();

    const Handle = SortableHandle(() => <span className={style.move} />);

    // 选择题的正确答案
    function optionsAnswer(options: OptionsType[]) {
        return options
            .map((item, index) => ({
                isRight: item.isRight,
                char: indexToChar(index)
            }))
            .filter(item => item.isRight)
            .map(item => item.char)
            .join("");
    }

    // 判断题正确答案
    function judgeAnswer(options: OptionsType[]) {
        return options
            .filter(item => item.isRight)
            .map(item => item.description)
            .join("");
    }

    function onDeleteClick() {
        Modal.confirm({ content: "确定要删除这道小题吗?", onOk: onDelete });
    }

    function onOk(value: QuestionType) {
        checkQuestion(value);
        onChange?.(value);
    }

    return (
        <div className={classNames(style.root, { [style.expand]: expand })}>
            <span className={style.index}>({index + 1}). </span>
            <span className={style.info}>
                (
                {value &&
                    BaseQuestionTypeMap[
                        value.queTypeId as keyof typeof BaseQuestionTypeMap
                    ]}
                , {value?.score}分)
            </span>

            <div className={style.tools}>
                {!expand && <Handle />}
                <Button
                    type="link"
                    className={style.edit}
                    onClick={modal.open}
                />
                <Button
                    type="link"
                    className={style.delete}
                    onClick={onDeleteClick}
                />
                <Button
                    type="link"
                    className={style.more}
                    onClick={() => setExpand(!expand)}>
                    详情
                </Button>
            </div>

            <div className={style.title}>
                {strCut(
                    htmlToPlain(value?.contentText || "", replaceImgTag),
                    200
                )}
            </div>

            {expand && (
                <div className={style.body}>
                    <div
                        className={style.content}
                        dangerouslySetInnerHTML={{
                            __html: value?.contentText || ""
                        }}
                    />

                    {value && "options" in value && (
                        <div className={style.options}>
                            {value.options.map((item, index) => (
                                <div key={item.key}>
                                    {indexToChar(index)}.
                                    {htmlToPlain(
                                        item.description,
                                        replaceImgTag
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    {value && "judgeOptions" in value && (
                        <div className={style.options}>
                            {value.judgeOptions.map(item => (
                                <div key={item.key}>{item.description}</div>
                            ))}
                        </div>
                    )}

                    {value &&
                        value.queTypeId !== BaseQuestionType.Subjective && (
                            <div className={style.answer}>
                                正确答案:
                                {value && "options" in value && (
                                    <span>{optionsAnswer(value.options)}</span>
                                )}
                                {value && "judgeOptions" in value && (
                                    <span>
                                        {judgeAnswer(value.judgeOptions)}
                                    </span>
                                )}
                                {value && "blanks" in value && (
                                    <div className={style.answerContent}>
                                        {value.blanks.map((item, index) => (
                                            <div>
                                                填空{index + 1}.{" "}
                                                {item.description}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                    {value && "analysis" in value && (
                        <div className={style.subjectiveAnswer}>
                            参考答案 <span>(活动结束后学生可见)</span>
                            <div
                                className={style.answerContent}
                                dangerouslySetInnerHTML={{
                                    __html: value.analysis
                                }}
                            />
                        </div>
                    )}
                </div>
            )}

            {modal.render(
                <AddTopicModal
                    title={
                        <SubModalTitle
                            title="编辑小题"
                            parentTitle="修改题目"
                        />
                    }
                    types={subTypes}
                    initialValue={value}
                    onOk={onOk}
                    afterClose={onUpdate}
                />
            )}
        </div>
    );
}
